import React, { useState } from "react";
import { Message } from "primereact/message";
import { Button } from "primereact/button";
import { backfillPerformance } from "../../../services/clearing/station-performance";

interface NeverComputedNoticeProps {
  months: string[];
  onComputed: () => void;
}

/**
 * Shown when a period has never been aggregated.
 *
 * Deliberately not an error. "No data" and "never ran" are different facts: an empty table would tell the
 * reader the fleet did nothing, when in truth the job simply has not run for this month. The remedy is
 * one click, so it is offered here rather than described.
 */
const NeverComputedNotice: React.FC<NeverComputedNoticeProps> = ({ months, onComputed }) => {
  const [running, setRunning] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const handleCompute = async () => {
    setRunning(true);
    setFailed(null);
    const from = months.slice().sort()[0];
    const to = months.slice().sort()[months.length - 1];
    const result = await backfillPerformance({ from, to });
    setRunning(false);
    if (result?.success) onComputed();
    else setFailed(result?.message ?? "The backfill could not be started.");
  };

  return (
    <div className="flex flex-column gap-3">
      <Message
        severity="info"
        text={`${months.join(", ")} ${months.length === 1 ? "has" : "have"} not been aggregated yet. This is not the same as having no activity — the figures are simply unknown until the job runs.`}
      />
      <div>
        <Button
          label={`Compute ${months.join(", ")}`}
          icon="pi pi-refresh"
          loading={running}
          onClick={handleCompute}
        />
      </div>
      {failed && <Message severity="error" text={failed} />}
    </div>
  );
};

export default NeverComputedNotice;
